import { useCallback, useMemo, useState } from "react";

import { EngagementOverviewTable } from "app/components/React/EngagementOverviewTable/EngagementOverviewTable";
import { DeviceDistribution } from "app/pages/website-analysis/TrafficAndEngagement/Components/Compare/DeviceDistribution/DeviceDistribution";
import { TotalVisits } from "app/pages/website-analysis/TrafficAndEngagement/Components/Compare/TotalVisits/TotalVisits";
import { WebsiteRank } from "app/pages/website-analysis/TrafficAndEngagement/Components/Compare/WebsiteRank/WebsiteRank";
import type {
    ITrafficCompareModeProps,
    IWebsiteAnalysisTrafficRequestParams,
} from "app/pages/website-analysis/TrafficAndEngagement/types";
import { RankByMode } from "app/pages/website-analysis/TrafficAndEngagement/types";
import { proNavigator } from "single-spa/react-app/ProNavigator";

import {
    TrafficAndEngagementHighLevelDataInner,
    type TrafficAndEngagementParams,
} from "../../TrafficAndEngagementHighLevelData";
import { OVERVIEW_TABLE_ENDPOINT } from "../../UtilityFunctionsAndConstants/UtilityFunctions";
import { MarginTopWrapper, TopPageWidgetsRow, EngagementOverviewTableSeparator } from "../StyledComponents";

import { useCompareDeviceDistributionData } from "./DeviceDistribution/useCompareDeviceDistributionData";
import { useCompareTotalVisitsData } from "./TotalVisits/useCompareTotalVisitsData";
import { useCompareWebRanksData } from "./WebsiteRank/useCompareWebRanksData";
import { getWebsiteRankSubtitleFilters } from "./WebsiteRank/websiteRankSubtitleFilters";

export const TrafficCompareModeContainer = (props: ITrafficCompareModeProps) => {
    const { showGAApprovedData, apiParams, isGAPrivate, showBetaBranchData } = props;
    const safeSubtitleFilters = Array.isArray(props.subtitleFilters)
        ? (props.subtitleFilters as Array<Record<string, unknown>>)
        : [];
    const safeParams = (props.params ?? {}) as TrafficAndEngagementParams;
    const { country, from, to, isWWW, isWindow, key, webSource } = apiParams;
    const apiParamsObj = useMemo(() => {
        return {
            country,
            from,
            to,
            includeSubDomains: !isWWW,
            isWindow,
            keys: key,
            timeGranularity: "Monthly",
            webSource,
        };
    }, [country, from, to, isWWW, isWindow, key, webSource]) as IWebsiteAnalysisTrafficRequestParams;

    const compareApiParams = useMemo(() => {
        return {
            ...apiParamsObj,
            ["ShouldGetVerifiedData"]: showGAApprovedData,
            beta: !!showBetaBranchData,
        };
    }, [apiParamsObj, showGAApprovedData, showBetaBranchData]) as IWebsiteAnalysisTrafficRequestParams;

    const totalVisitsMetricData = useCompareTotalVisitsData(compareApiParams);

    const devicesDistributionMetricData = useCompareDeviceDistributionData(compareApiParams);

    const [selectedRankByMode, setSelectedRankByMode] = useState<RankByMode>(RankByMode.Country);

    const webRanksData = useCompareWebRanksData(apiParamsObj, selectedRankByMode);

    const getRedirectUrl = useCallback((redirectCountry: string, category: string) => {
        if (proNavigator.isSalesSolution()) {
            return undefined;
        }
        return proNavigator.getStateUrl("digitalsuite_marketresearch_webmarketanalysis_mapping", {
            country: redirectCountry,
            duration: "1m",
            category,
        });
    }, []);

    const rankSubtitleFilters = useMemo(() => {
        return getWebsiteRankSubtitleFilters({
            data: webRanksData.data,
            selectedRankByMode,
            subtitleFilters: safeSubtitleFilters,
            defaultCountry: apiParams.country,
            getRedirectUrl,
        });
    }, [apiParams.country, getRedirectUrl, selectedRankByMode, safeSubtitleFilters, webRanksData.data]);

    const onRankModeChange = useCallback((item: { id: RankByMode }) => {
        setSelectedRankByMode(item?.id);
    }, []);

    const isDevicesDistributionLoading =
        devicesDistributionMetricData.isLoading || devicesDistributionMetricData.isFetching;

    const isTotalVisitsLoading = totalVisitsMetricData.isLoading || totalVisitsMetricData.isFetching;
    const isWebRanksLoading = webRanksData.isLoading || webRanksData.isFetching;

    const engagementOverviewParams = useMemo(() => {
        return {
            showGAApprovedData,
            routing: {
                params: {
                    country,
                    webSource,
                    key,
                    isWWW: safeParams.isWWW,
                    duration: safeParams.duration,
                },
            },
            showBetaBranchData,
            hideTopBorderRadius: true,
            endpoint: OVERVIEW_TABLE_ENDPOINT,
        };
    }, [showGAApprovedData, country, webSource, key, safeParams.isWWW, safeParams.duration, showBetaBranchData]);

    return (
        <div>
            <TopPageWidgetsRow>
                <TotalVisits
                    data={totalVisitsMetricData.data}
                    subtitleFilters={safeSubtitleFilters}
                    hasGA={showGAApprovedData}
                    isGAPrivate={isGAPrivate}
                    isLoading={isTotalVisitsLoading}
                    params={safeParams}
                    showBetaBranchData={showBetaBranchData}
                />
                <DeviceDistribution
                    data={devicesDistributionMetricData.data}
                    subtitleFilters={safeSubtitleFilters}
                    hasGA={showGAApprovedData}
                    isGAPrivate={isGAPrivate}
                    isLoading={isDevicesDistributionLoading}
                    params={apiParams}
                    showBetaBranchData={showBetaBranchData}
                />
                <WebsiteRank
                    data={webRanksData.data}
                    subtitleFilters={rankSubtitleFilters}
                    onRankModeChange={onRankModeChange}
                    selectedRankMode={selectedRankByMode}
                    isLoading={isWebRanksLoading}
                />
            </TopPageWidgetsRow>
            <MarginTopWrapper topMargin={22}>
                <TrafficAndEngagementHighLevelDataInner
                    params={safeParams}
                    showGAApprovedData={showGAApprovedData}
                    showBetaBranchData={showBetaBranchData}
                />
            </MarginTopWrapper>
            <EngagementOverviewTableSeparator />
            <EngagementOverviewTable {...engagementOverviewParams} />
        </div>
    );
};
